import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { FaArrowRight, FaExclamationTriangle, FaInfoCircle } from 'react-icons/fa';
import { previewIds, savePins, setPropertyCode, patchSession } from '../../../../services/onboardingWizardService';

const emptyPreview = { rows: [], property_code: '', property_code_default: '', property_code_override: '' };

function Step4ReviewIds({ session, propertyId, pins: initialPins, categories, onApplyBundle, onAdvance, onFlush }) {
  const [pins, setPins] = useState(initialPins || []);
  const [preview, setPreview] = useState(emptyPreview);
  const [loading, setLoading] = useState(true);
  const [codeDraft, setCodeDraft] = useState('');
  const [savingCode, setSavingCode] = useState(false);
  const saveTimer = useRef(null);
  const prefixInitialised = useRef(false);

  useEffect(() => { setPins(initialPins || []); }, [initialPins?.length]);

  useEffect(() => {
    prefixInitialised.current = false;
  }, [session.id]);

  const reloadPreview = async () => {
    setLoading(true);
    try {
      const r = await previewIds(session.id);
      setPreview(r || emptyPreview);
      if (!prefixInitialised.current) {
        setCodeDraft((r && r.property_code) || '');
        prefixInitialised.current = true;
      }
    } catch (e) { toast.error(e.message || 'Failed to preview'); }
    finally { setLoading(false); }
  };

  useEffect(() => { reloadPreview(); /* eslint-disable-next-line */ }, [session.id, pins.length]);

  const saveSessionPrefix = async () => {
    let code = (codeDraft || '').trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (code.length > 16) return toast.error('Session prefix max 16 characters');

    if (!code) {
      if (!(preview.property_code_default || '').trim()) {
        return toast.error('Enter a prefix or save a property default first');
      }
      setSavingCode(true);
      try {
        await patchSession(session.id, { property_code_override: null });
        toast.success('Using property default prefix for this session');
        prefixInitialised.current = false;
        await reloadPreview();
      } catch (e) { toast.error(e.message || 'Failed to save'); }
      finally { setSavingCode(false); }
      return;
    }

    setSavingCode(true);
    try {
      const defNorm = (preview.property_code_default || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
      const sameAsDefault = defNorm && code === defNorm;
      await patchSession(session.id, { property_code_override: sameAsDefault ? null : code });
      toast.success(sameAsDefault ? 'Using property default prefix' : `Session prefix set to ${code}`);
      prefixInitialised.current = false;
      await reloadPreview();
    } catch (e) { toast.error(e.message || 'Failed to save'); }
    finally { setSavingCode(false); }
  };

  const saveAsPropertyDefault = async () => {
    const code = (codeDraft || '').trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (!code) return toast.error('Enter a prefix first');
    if (code.length > 10) return toast.error('Property default codes are max 10 characters');
    setSavingCode(true);
    try {
      await setPropertyCode(propertyId, code);
      await patchSession(session.id, { property_code_override: null });
      toast.success(`Property default set to ${code}`);
      prefixInitialised.current = false;
      await reloadPreview();
    } catch (e) { toast.error(e.message || 'Failed to save'); }
    finally { setSavingCode(false); }
  };

  const catsById = useMemo(() => Object.fromEntries((categories || []).map(c => [c.id, c])), [categories]);

  const byPinId = useMemo(() => Object.fromEntries((preview.rows || []).map(r => [r.pin_id, r])), [preview]);

  const issueCount = (preview.rows || []).filter(r => !r.generated_asset_id).length;

  // Step 3 stashes classification metadata as JSON in name_reference.
  // Extract the optional displayName for editing; preserve the JSON on save.
  const parseClassification = (raw) => {
    if (!raw || typeof raw !== 'string' || !raw.trim().startsWith('{')) return null;
    try {
      const obj = JSON.parse(raw);
      if (obj && (obj.subtype || obj.material || obj.fixing)) return obj;
    } catch (_) {}
    return null;
  };

  const getDisplayRef = (pin) => {
    const cls = parseClassification(pin.name_reference);
    if (cls) return cls.displayName || '';
    return pin.name_reference || '';
  };

  const serialiseRef = (pin, value) => {
    const cls = parseClassification(pin.name_reference);
    if (cls) return JSON.stringify({ ...cls, displayName: value });
    return value;
  };

  const onRefChange = (pinId, value) => {
    setPins(prev => prev.map(p => p.id === pinId ? { ...p, name_reference: serialiseRef(p, value) } : p));
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      const next = pins.map(p => p.id === pinId ? { ...p, name_reference: serialiseRef(p, value) } : p);
      try { const bundle = await savePins(session.id, next); onApplyBundle(bundle); }
      catch (_) { /* tolerate transient */ }
    }, 500);
  };

  const continueNext = async () => {
    if (issueCount > 0) return toast.error(`${issueCount} pin${issueCount > 1 ? 's' : ''} couldn't generate an ID — resolve before continuing`);
    if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = null; }
    await onFlush();
    onAdvance(5);
  };

  return (
    <div className="ow-step-body ow-step-wide">
      <section className="ow-card ow-card-tight">
        <header className="ow-card-head ow-plan-head">
          <div>
            <div className="ow-card-kicker">Step 4 · Review IDs</div>
            <h2 className="ow-card-title">Check generated asset IDs</h2>
            <p className="ow-card-sub ow-plan-hint">
              The <strong>selected building</strong> supplies the ID prefix <strong>{preview.property_code || '…'}</strong> (from its saved code, or generated from the building name when you open this flow).
              Category code and 3-digit sequence follow. Use a session-only prefix below only when another floor or plan needs a different code. Optional name per pin.
            </p>
          </div>
          <div className="ow-plan-counters">
            <div className="ow-counter"><strong>{pins.length}</strong><em>assets</em></div>
            <div className={`ow-counter ${issueCount ? 'ow-counter-warn' : 'ow-counter-alt'}`}>
              <strong>{issueCount ? issueCount : (preview.rows || []).filter(r => r.generated_asset_id).length}</strong>
              <em>{issueCount ? 'issues' : 'ready'}</em>
            </div>
          </div>
        </header>

        {!loading && (
          <div className={`ow-banner ow-banner-inline ${preview.property_code ? 'ow-banner-info' : 'ow-banner-warn'}`}>
            {preview.property_code ? <FaInfoCircle /> : <FaExclamationTriangle />}
            <div className="ow-banner-copy">
              <strong>ID prefix.</strong>
              <span>
                {' '}
                This property’s default prefix is filled in automatically from the building you chose.
                <strong> Apply for this session</strong> only to override for one floor or plan (e.g. <code>UCL44L2</code>).
                {(preview.property_code_default || '').trim()
                  ? <> Building code: <code>{preview.property_code_default}</code>.</>
                  : <> No building code could be resolved — enter one below or save as the property default.</>}
                {(preview.property_code_override || '').trim() && (
                  <> Session override active: <code>{preview.property_code_override}</code>.</>
                )}
              </span>
            </div>
            <div className="ow-banner-inline-form">
              <input
                className="ow-input ow-input-mono"
                maxLength={16}
                autoFocus={!preview.property_code}
                placeholder={(preview.property_code_default || '').trim() ? `e.g. ${preview.property_code_default}L2` : 'e.g. UCL44 or UCL44L2'}
                value={codeDraft}
                onChange={e => setCodeDraft(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 16))}
                onKeyDown={e => { if (e.key === 'Enter') saveSessionPrefix(); }}
              />
              <button type="button" className="ow-btn ow-btn-primary" disabled={savingCode} onClick={saveSessionPrefix}>
                {savingCode ? 'Saving…' : 'Apply for this session'}
              </button>
              <button
                type="button"
                className="ow-btn ow-btn-ghost"
                disabled={savingCode || !codeDraft.trim()}
                title="Stores on the property record (max 10 characters). New sessions use this unless you override."
                onClick={saveAsPropertyDefault}
              >
                Save as property default
              </button>
            </div>
          </div>
        )}

        <div className="ow-review-wrap">
          {loading ? (
            <div className="ow-loading" style={{ padding: 40 }}>Previewing IDs…</div>
          ) : (
            <table className="ow-review-table">
              <thead>
                <tr>
                  <th style={{ width: '72px' }}>Pin</th>
                  <th>Category</th>
                  <th style={{ width: '240px' }}>Generated asset ID</th>
                  <th>Name / reference (optional)</th>
                </tr>
              </thead>
              <tbody>
                {pins.map(pin => {
                  const row = byPinId[pin.id];
                  const cat = catsById[pin.category_id];
                  const id = row?.generated_asset_id;
                  return (
                    <tr key={pin.id} className={!id ? 'has-issue' : ''}>
                      <td><span className="ow-review-pin-num">#{pin.temp_number}</span></td>
                      <td>
                        {cat ? (
                          <span className="ow-review-cat">
                            <span className="ow-cat-swatch" style={{ background: cat.color }} />
                            <span>{cat.name}</span>
                          </span>
                        ) : (
                          <em className="ow-muted">Uncategorised</em>
                        )}
                      </td>
                      <td>
                        {id ? (
                          <code className="ow-review-id">{id}</code>
                        ) : (
                          <span className="ow-review-issue"><FaExclamationTriangle /> {reasonLabel(row?.reason)}</span>
                        )}
                      </td>
                      <td>
                        <input className="ow-input"
                               placeholder="e.g. near west stair"
                               value={getDisplayRef(pin)}
                               onChange={e => onRefChange(pin.id, e.target.value)} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <footer className="ow-card-foot">
          <span className="ow-foot-note">
            {issueCount > 0
              ? `${issueCount} issue${issueCount > 1 ? 's' : ''} to resolve before confirming.`
              : 'All IDs generated successfully.'}
          </span>
          <button className="ow-btn ow-btn-primary" onClick={continueNext} disabled={loading || issueCount > 0 || !pins.length}>
            Continue to confirm <FaArrowRight />
          </button>
        </footer>
      </section>
    </div>
  );
}

function reasonLabel(r) {
  switch (r) {
    case 'no_category': return 'No category assigned';
    case 'no_property_code': return 'Property code not set';
    case 'no_tokens_matched': return 'No tokens matched category name';
    default: return 'Could not generate';
  }
}

export default Step4ReviewIds;
